import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import './SmartSavings.css';
import { resetUpdateUserNotificationConsentState, updateUserNotificationConsentStateRequest } from '../../../store/brotherPlus/UserNotificationConsent/UpdateUserNotificationConsentStateSlice';
import { getUserNotificationConsentDetailsRequest } from '../../../store/brotherPlus/UserNotificationConsent/GetUserNotificationConsentDetailsSlice';
import { APP, DASHBOARD, FAILURE, SUCCESS, WEB } from '../../../constants/AppConstants';
import SmartSavingsModal from './SmartSavingsModal';
import { Modal, ModalHeader } from "reactstrap";
import { ReactComponent as OptInArrow } from "../../../assets/images/smartsavingsoptinarrow.svg";
import { ReactComponent as ModalCloseIcon } from "../../../assets/images/mydevices/modal-close-icon.svg";
import Loader from '../../../components/shared/loader/Loader';
import EcoProButton from '../../../components/shared/button/button';
import { evaluateConsent, buildConsentPayload } from '../../../utils/consentService';
import { useUserNotificationConsent } from '../../../utils/CustomHooks/useUserNotificationConsent';

const INITIAL_CONSENT = { email: false, inApp: false };

const SmartSavings = ({ variant = 'dashboard' }) => {
  const { t } = useTranslation(['translation']);
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [consent, setConsent] = useState(INITIAL_CONSENT);

  // Stores the last server-confirmed consent without causing re-renders
  const savedConsent = useRef(INITIAL_CONSENT);
  // Ref for synchronous guard in handleSave; state mirror drives the disabled prop
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    loading: getConsentLoading,
    userNotificationConsentDetails,
    emailConsent,
    inAppConsent,
    error: getConsentError
  } = useUserNotificationConsent();

  const { userNotificationConsentState, loading: putConsentLoading } =
  useSelector((state) => state.BROTHERPLUS.updateUserNotificationConsentState);

  const hasChanges = consent.email !== savedConsent.current.email || consent.inApp !== savedConsent.current.inApp;

  useEffect(()=>{
       dispatch(getUserNotificationConsentDetailsRequest());
  },[dispatch])
  // Initialize local state from the latest server response
  useEffect(() => {
    if (variant === 'myAccount') {
      const next = {
        email: emailConsent ?? false,
        inApp: inAppConsent ?? false,
      };

      savedConsent.current = next;
      setConsent(next);
      // Full round-trip complete — unlock the save button
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
    // eslint-disable-next-line
  }, [userNotificationConsentDetails, variant]);

  // If the GET fails, clear the submission lock so the save button is not permanently disabled
  useEffect(() => {
    if (!getConsentError) return;
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  }, [getConsentError]);

  // Refresh data on success and restore the previous state on failure
  useEffect(() => {
    const status = userNotificationConsentState?.actionStatus?.toUpperCase();
    if (!status) return;

    if (status === SUCCESS) {
      dispatch(getUserNotificationConsentDetailsRequest());
    }

    if (status === FAILURE) {
      if (variant === 'myAccount') {
        setConsent({ ...savedConsent.current });
      }
      // Unlock the save button so the user can retry after a failure
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }

    dispatch(resetUpdateUserNotificationConsentState());
  }, [userNotificationConsentState, dispatch, variant, t]);

  const handleConsentChange = (key) => {
    setConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  let shouldShowBanner = false;
  if(userNotificationConsentDetails){
    const result = evaluateConsent({
      consentValue: emailConsent,
      enrolmentSource: DASHBOARD,
      isDashboardInAppValue: inAppConsent
    });
    shouldShowBanner = result.shouldShowBanner;
  }

  const handleSave = () => {
    if (!hasChanges || putConsentLoading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Build a payload containing only the keys whose toggles actually changed.
    // Each source (WEB / APP) is handled separately so no unintended key is sent.
    const payload = {
      ...(consent.email !== savedConsent.current.email &&
        buildConsentPayload({ userConsent: { emailConsent: consent.email }, enrolmentSource: WEB }).payload),
      ...(consent.inApp !== savedConsent.current.inApp &&
        buildConsentPayload({ userConsent: { appConsent: consent.inApp }, enrolmentSource: APP }).payload),
    };

    dispatch(updateUserNotificationConsentStateRequest(payload));
  };

  const renderNotificationToggle = (id, labelKey, consentKey) => (
    <div className="my-account-smart-savings-toggle-wrapper">
      <div className="my-account-smart-savings-label">
        {t(labelKey)}
      </div>

      <div className="my-account-smart-savings-toggle">
        <div className="toggle-switch">
          <input
            type="checkbox"
            id={id}
            className="toggle-input"
            checked={consent[consentKey]}
            onChange={() => handleConsentChange(consentKey)}
            disabled={putConsentLoading}
          />
          <label htmlFor={id} className="toggle-label" />
        </div>
      </div>
    </div>
  );


  if (variant === 'dashboard') {
    return (
      <>
        {!getConsentLoading &&
          shouldShowBanner && (
            <div className="smart-savings-container" style={{ position: "relative" }}>
              <div className="text-container">
                <div className="title-text">
                  {t('CONNECT_DASHBOARD.SMART_SAVINGS.TITLE')}
                </div>

                <div className="subheader-smartsavings">
                  {t('CONNECT_DASHBOARD.SMART_SAVINGS.SUB_TITLE')}
                </div>

                <div className="smartsavings-content">
                  {t('CONNECT_DASHBOARD.SMART_SAVINGS.CONTENT')}
                </div>

                <button
                  className="optin-button mt-10"
                  onClick={() => setShowModal(true)}
                >
                  Opt In
                  <OptInArrow />
                </button>

                <Modal
                  isOpen={showModal}
                  className="smart-savings-modal-container manage-address-modal"
                  fullscreen
                >
                  <div className="modal-close-icon">
                    <ModalHeader
                      close={<ModalCloseIcon onClick={() => setShowModal(false)} />}
                    />
                  </div>

                  <Suspense>
                    {showModal && (
                      <SmartSavingsModal
                        onClose={() => setShowModal(false)}
                      />
                    )}
                  </Suspense>
                </Modal>
              </div>
            </div>
          )}
      </>
    );
  }

  if (variant === 'myAccount') {
    return (
      <div className="my-account-smart-savings-container">
        <div className="my-account-smart-savings-header">
          {t('BROTHER_PLUS.SMART_SAVINGS.HEADER')}
        </div>

        {renderNotificationToggle(
          "email-toggle",
          "BROTHER_PLUS.SMART_SAVINGS.EMAIL_NOTIFICATIONS",
          "email"
        )}

        {renderNotificationToggle(
          "inapp-toggle",
          "BROTHER_PLUS.SMART_SAVINGS.INAPP_NOTIFICATIONS",
          "inApp"
        )}

        <div className="my-account-smart-savings-save-btn-wrapper">
          <EcoProButton
            cname="my-account-smart-savings-save-btn"
            onBtnClick={handleSave}
            disabled={!hasChanges || putConsentLoading || isSubmitting}
          >
            {putConsentLoading
              ? t('BROTHER_PLUS.SMART_SAVINGS.SAVING')
              : t('BROTHER_PLUS.SMART_SAVINGS.SAVE')}
          </EcoProButton>
        </div>

        {putConsentLoading && <Loader setOverlay={true} />}
      </div>
    );
  }

  return null;
};

export default SmartSavings;